import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ApiServiceUtils} from './api-service-utils';
import * as _ from 'lodash';
import * as settings from '../settings/settings.json';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class GenericService {
  review;
  reviewers;
  reviewSubPrograms;
  constructor(private apiServiceUtils: ApiServiceUtils, private http: HttpClient) {}

  public isLocalHost(): boolean {
    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1';
  }

  public findAll(listName, condition?) {
    if (condition) {
      listName = listName + '?filter=' + condition;
    }
    return this.http.get(`${this.apiServiceUtils.getApiUrl()}/${listName}`);
  }

  public findById(listName, id) {
    return this.http.get(`${this.apiServiceUtils.getApiUrl()}/${listName}/${id}`);
  }

  public update(listName, id, item) {
    return this.http.patch(`${this.apiServiceUtils.getApiUrl()}/${listName}/${id}`, item);
  }

  public create(listName, item) {
    return this.http.post(`${this.apiServiceUtils.getApiUrl()}/${listName}`, item);
  }

  public createMultiple(listName, items) {
    return this.http.post(`${this.apiServiceUtils.getApiUrl()}/${listName}/batch`, items);
  }

  public delete(listName, id) {
    return this.http.delete(`${this.apiServiceUtils.getApiUrl()}/${listName}/${id}`);
  }

  public uploadFile(listName, file) {
    return this.http.post(`${this.apiServiceUtils.getApiUrl()}/${listName}/upload`, file);
  }

  private downloadFile(listName, fileName) {
    return this.http.get(`${this.apiServiceUtils.getApiUrl()}/${listName}/download?fileName=${fileName}`, {
      responseType: 'arraybuffer'
    });
  }

  private convertArrayBufferToUrl(arrayBuffer: ArrayBuffer, fileName: string, fileExtension: string): string {
    let mimeType = '';
    switch (fileExtension) {
      case 'pdf':
        mimeType = 'application/pdf';
        break;
      case 'txt':
        mimeType = 'text/plain';
        break;
      case 'ppt':
      case 'pptx':
        mimeType = 'application/vnd.ms-powerpoint';
        break;
      case 'doc':
      case 'docx':
        mimeType = 'application/msword';
        break;
      case 'xls':
      case 'xlsx':
        mimeType = 'application/vnd.ms-excel';
        break;
      case 'jpg':
      case 'jpeg':
        mimeType = 'image/jpeg';
        break;
      case 'png':
        mimeType = 'image/png';
        break;
      default:
        mimeType = 'application/octet-stream';
    }

    if (mimeType.startsWith('image/')) {
      let binary = '';
      const bytes = new Uint8Array(arrayBuffer);
      const len = bytes.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64String = btoa(binary);
      return `data:${mimeType};base64,${base64String}`;
    } else {
      const blob = new Blob([arrayBuffer], {type: mimeType});
      return URL.createObjectURL(blob);
    }
  }

  public fetchAndProcessFile(path): Observable<string> {
    if (!path) {
      return of('');
    }

    // Relative/local asset paths — use as-is (e.g. assets/images/Capture111.PNG).
    if (!/^https?:\/\//i.test(path)) {
      return of(path);
    }

    // On localhost, serve known SharePoint images from bundled assets
    // (SharePoint URLs fail in the browser without interactive auth / CORS).
    if (this.isLocalHost()) {
      try {
        const localFileName = decodeURIComponent(new URL(path).pathname.split('/').pop() || '');
        if (/^capture111\./i.test(localFileName)) {
          return of(`assets/images/${localFileName}`);
        }
        if (/^staff-img\./i.test(localFileName)) {
          return of('assets/staff-img.png');
        }
      } catch {
        // fall through to SharePoint download
      }
    }

    try {
      const parsedUrl = new URL(path);
      const pathname = parsedUrl.pathname;
      const fileName = pathname.substring(pathname.lastIndexOf('/') + 1);
      const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
      const pathWithoutFilename = pathname.substring(0, pathname.lastIndexOf('/'));
      const documentPath = this.apiServiceUtils.getFileStorageUrl() + settings.lists.documents.name;

      if (documentPath !== parsedUrl.origin + pathWithoutFilename) {
        window.open(path, '_blank');
        return of('');
      }
      return this.downloadFile(settings.lists.documents.name, fileName).pipe(
        map(arrayBuffer => {
          return this.processAndOpenFile(arrayBuffer, fileName, fileExtension);
        })
      );
    } catch (error) {
      console.error('Error parsing URL in fetchAndProcessFile:', error);
      return of('');
    }
  }

  private processAndOpenFile(arrayBuffer: ArrayBuffer, fileName: string, fileExtension: string): string {
    const url = this.convertArrayBufferToUrl(arrayBuffer, fileName, fileExtension);
    if (!url.startsWith('data:')) {
      window.open(url, '_blank');
      URL.revokeObjectURL(url);
    }
    return url;
  }
  public clearAllCache() {
    this.review = null;
    this.reviewers = null;
    this.reviewSubPrograms = null;
  }

  public setReviewCache(review) {
    this.review = review;
  }

  public getReviewCache() {
    return this.review;
  }

  public setReviewersCache(reviewers) {
    this.reviewers = reviewers;
  }

  public getReviewersCache() {
    return this.reviewers;
  }

  public setReviewSubProgramsCache(reviewSubPrograms) {
    this.reviewSubPrograms = reviewSubPrograms;
  }

  public getReviewSubProgramsCache() {
    return this.reviewSubPrograms;
  }

  public leftNavigationMenu(): any {
    return {
      sd: [
        {title: 'review overview', url: 'overview', disable: false},
        {title: 'review milestones', url: 'milestones', disable: false},
        {title: 'review materials', url: 'materials', disable: false},
        // tslint:disable-next-line: quotemark
        {title: "Staff Participating in the 'QUAD' Review", url: 'programStaff', disable: false},
        {title: 'reviewers', url: 'reviewers', disable: false},
        {title: 'reports', url: 'reports', disable: false}
      ],
      sdIncomplete: [{title: 'review overview', url: 'overview', disable: false}],
      osd: [
        {title: 'review overview', url: 'overview', disable: false},
        {title: 'review milestones', url: 'milestones', disable: false},
        {title: 'review materials', url: 'materials', disable: false},
        // tslint:disable-next-line: quotemark
        {title: "Staff Participating in the 'QUAD' Review", url: 'programStaff', disable: false},
        {title: 'reviewers', url: 'reviewers', disable: false}
      ],
      osdIncomplete: [{title: 'review overview', url: 'overview', disable: false}],
      reviewer: [
        {title: 'review overview', url: 'dashboard', disable: false},
        {title: 'review materials', url: 'materials', disable: false},
        {title: 'reviewers', url: 'reviewers', disable: false},
        {title: 'critique sheets', url: 'critiqueSheets', disable: false},
        {title: 'report', url: 'programReport', disable: false}
      ],
      reviewerIncomplete: [{title: 'review overview', url: 'dashboard', disable: false}]
    };
  }

  public convertMilestonesToEvents(milestones) {
    const milestoneEvents = [];
    for (const item of milestones) {
      const milestone = {name: '', date: null, color: ''};
      milestone.name = item[settings.lists.milestones.fields.milestoneType].value;
      milestone.date = new Date(item[settings.lists.milestones.fields.dueDate]);
      const completionDate =
        item[settings.lists.milestones.fields.completionDate] !== null
          ? new Date(item[settings.lists.milestones.fields.completionDate])
          : item[settings.lists.milestones.fields.completionDate];
      if (completionDate === null && new Date() > milestone.date) {
        milestone.color = '#ee5b42'; // red
      } else if (completionDate !== null && completionDate > milestone.date) {
        milestone.color = '#f2c12e'; // yellow
      } else if (completionDate !== null) {
        milestone.color = '#22b573'; // green
      } else {
        milestone.color = '#9c9a9c'; // gray
      }
      // update milestone indicator date to completionDate if exits or keep due date
      if (completionDate !== null) {
        milestone.date = new Date(item[settings.lists.milestones.fields.completionDate]);
      }
      milestoneEvents.push(milestone);
    }
    return milestoneEvents;
  }

  public convertMilestonesToCompletedByCodes(milestones) {
    const result = {};
    for (const milestone of milestones) {
      result[milestone.milestoneTypeCode] = milestone.completionDate !== null ? true : false;
    }
    return result;
  }

  public findAuthInfo() {
    const baseUrlAuth = this.apiServiceUtils.authUrl;
    const tenant = this.apiServiceUtils.tenant;
    const AuthBase = `auth/${tenant}/me`;
    const url = [baseUrlAuth, AuthBase].join('/');
    return this.http.get(`${url}`);
  }

  public findPeople(tenantId, providerId, filter) {
    const baseUrlAuth = this.apiServiceUtils.authUrl;
    const AuthProviders = 'providers';
    const AuthUsers = 'users?filter=';
    const AuthBase = 'auth/user-directories/tenants';
    const url = [baseUrlAuth, AuthBase, tenantId.toString(), AuthProviders, providerId.toString(), AuthUsers].join('/');
    return this.http.get(`${url}${filter}`);
  }
}
